import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { closeModalAction } from '../../app/appActions';
import {
    getAppFeatures,
    getOpenedModalParamsSelector,
    makeSelectAppLoggedIn,
} from '../../app/appSelectors';
import { useResponsive } from '../../components/responsive';
import { MODALS_NAME } from './modalsConstants';
import TutorialIntroModal from '../Tutorials/components/TutorialIntroModal/TutorialIntroModal';
import TransactionsModalLoadable from './TransactionsModal/TransactionsModalLoadable';
import SettingsModalLoadable from './SettingsModal/SettingsModalLoadable';
import ResetPasswordModalLoadable from './ResetPasswordModal/ResetPasswordModalLoadable';
import EmailConfirmedModalLoadable from './EmailConfirmedModal/EmailConfirmedModalLoadable';
import DepositSurveyModalLoadable from './DepositSurveyModal/DepositSurveyModalLoadable';
import PromocoeHelloJetvixModal from './PromocoeHelloJetvixModal';
import WithdrawCancelModal from './WithdrawCancelModal/WithdrawCancelModal';
import { LOCATION_COMPONENTS_SEARCH_PARAMS } from '../Location/locationConstants';
import UserStatusesModal from './UserStatusesModal/UserStatusesModal';
import WithdrawalLimitsModal from './WithdrawalLimitsModal/WithdrawalLimitsModal';
import UserStateInformationModal from '../UserStateInformation/UserStateInformationModal';
import RespawnStatusModal from './RespawnStatusModal/RespawnStatusModal';
import CashbackModal from './CashbackModal/CashbackModal';
import RespawnModal from './RespawnModal/RespawnModal';
import StatusCongratulationsModal from './StatusCongratulationsModal/StatusCongratulationsModal';
import AchievementModal from './AchievementModal/AchievementModal';
import { FEATURES } from '../../app/appConstants';
import RefillModalLoadable from './RefillModal/RefillModalLoadable';
import RespawnMotivationModalLoadable from './RespawnMotivationModal/RespawnMotivationModalLoadable';
import DeleteProfileModalLoadable from './DeleteProfileModal/DeleteProfileModalLoadable';
import RequestFeedbackModalLoadable from './RequestFeedbackModal/RequestFeedbackModalLoadable';
import DemoLimitsModalLoadable from '../DemoLimits/components/DemoLimitsModal/DemoLimitsModalLoadable';
import ReferralReceivedRewardModalLoadable from './ReferralReceivedRewardModal/ReferralReceivedRewardModalLoadable';
import VerifyRequestModalLoadable from './VerifyRequestModal/VerifyRequestModalLoadable';
import ReferralGiftRewardModalLoadable from './ReferralGiftRewardModal/ReferralGiftRewardModalLoadable';
import ReferralWarningResetProgressModalLoadable
    from './ReferralWarningResetProgressModal/ReferralWarningResetProgressModalLoadable';
import ShareReferralLinkModalLoadable
    from '../Referral/components/ShareReferralLinkModal/ShareReferralLinkModalLoadable';
import ShareReferralQrCodeModalLoadable
    from '../Referral/components/ShareReferralQrCodeModal/ShareReferralQrCodeModalLoadable';
import ReferralRespawnTimerModalLoadable
    from '../Referral/components/ReferralRespawnTimerModal/ReferralRespawnTimerModalLoadable';
import LeaderboardNotificationMessageDialogLoadable
    from './LeaderboardNotificationMessageDialog/LeaderboardNotificationMessageLeagueDialogLoadable';

export const MODALS_LOG_ACTION_TYPES = {
    OPEN: 'open' as const,
    TAB: 'tab' as const,
};

const selectAppLoggedIn = makeSelectAppLoggedIn();

const Modals = () => {
    const { isSmallMaxWidth, isMediumMaxWidth } = useResponsive();
    const dispatch = useDispatch();
    const closeModal = () => dispatch(closeModalAction());
    const loggedIn = useSelector(selectAppLoggedIn);
    const params = useSelector(getOpenedModalParamsSelector);
    const features = useSelector(getAppFeatures);
    const { search } = useLocation();
    const [openedName, openedTab] = useMemo(() => {
        const params = new URLSearchParams(search);
        return [params.get(LOCATION_COMPONENTS_SEARCH_PARAMS.MODAL), params.get('tab')];
    }, [search]);

    return <>
        {
            openedName === MODALS_NAME.CASHIER && loggedIn && !features[FEATURES.DEMO_MODE] &&
            <Modal show
                   onHide={closeModal}
                   backdrop={'static'}
                   centered
                   fullscreen={isMediumMaxWidth ? isMediumMaxWidth : undefined}
                   size={'xl'}>
                <TransactionsModalLoadable selectedItem={openedTab} closeModal={closeModal}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.PERSONAL && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}>
                <SettingsModalLoadable closeModal={closeModal}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.INTRO && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   size={'xl'}
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}>
                <TutorialIntroModal/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.RESET_PASSWORD && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}>
                <ResetPasswordModalLoadable closeModal={closeModal}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.EMAIL_CONFIRMED && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
                   contentClassName="pt-0"
            >
                <EmailConfirmedModalLoadable closeModal={closeModal}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.DEPOSIT_SURVEY && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <DepositSurveyModalLoadable/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.PROMOCODE_HELLO_JETVIX && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <PromocoeHelloJetvixModal/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.WITHDRAWAL_CANCEL && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <WithdrawCancelModal {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.USER_STATUSES && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <UserStatusesModal {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.WITHDRAWAL_LIMITS && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <WithdrawalLimitsModal {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.USER_STATE_INFORMATION && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <UserStateInformationModal/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.RESPAWN_STATUS && loggedIn &&
            <RespawnStatusModal
                show
                onClose={closeModal}
                {...params}
            />
        }
        {
            openedName === MODALS_NAME.CASHBACK && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <CashbackModal {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.RESPAWN && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   dialogClassName={'dialog-w-600'}
                   contentClassName={'modal-content-h-992'}
                   scrollable
            >
                <RespawnModal {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.STATUS_CONGRATULATIONS && loggedIn &&
            <StatusCongratulationsModal/>
        }
        {
            openedName === MODALS_NAME.ACHIEVEMENT && loggedIn &&
            <AchievementModal/>
        }
        {
            openedName === MODALS_NAME.REFILL && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   fullscreen={isSmallMaxWidth ? isSmallMaxWidth : undefined}
            >
                <RefillModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.RESPAWN_MOTIVATION && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
            >
                <RespawnMotivationModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.DELETE_PROFILE && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
            >
                <DeleteProfileModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.REQUEST_FEEDBACK && loggedIn &&
            <RequestFeedbackModalLoadable/>
        }
        {
            openedName === MODALS_NAME.REFERRAL_RECEIVED_REWARD && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   size={'lg'}
            >
                <ReferralReceivedRewardModalLoadable/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.VERIFY_REQUEST && loggedIn &&
            <VerifyRequestModalLoadable/>
        }
        {
            openedName === MODALS_NAME.REFERRAL_GIFT_REWARD && loggedIn &&
            <ReferralGiftRewardModalLoadable/>
        }
        {
            openedName === MODALS_NAME.DEMO_LIMITS && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
                   dialogClassName={'dialog-min-w-697'}
            >
                <DemoLimitsModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.REFERRAL_WARNING_RESET_PROGRESS && loggedIn &&
            <ReferralWarningResetProgressModalLoadable/>
        }
        {
            openedName === MODALS_NAME.REFERRAL_SHARE_LINK && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
            >
                <ShareReferralLinkModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.REFERRAL_SHARE_QR_CODE && loggedIn &&
            <Modal show
                   onHide={closeModal}
                   centered
            >
                <ShareReferralQrCodeModalLoadable {...params}/>
            </Modal>
        }
        {
            openedName === MODALS_NAME.REFERRAL_RESPAWN_TIMER && loggedIn &&
            <ReferralRespawnTimerModalLoadable {...params}/>
        }
        {
            openedName === MODALS_NAME.LEADERBOARD_NOTIFICATION_LEAGUE && loggedIn &&
            <LeaderboardNotificationMessageDialogLoadable/>
        }
    </>;
};

export default Modals;

